/**
 * Public CMS read hooks — articles, galleries, resources, climbing routes, about.
 * Maps backend documents to a display-friendly shape with a current-language convenience.
 */
import { useEffect, useState, useCallback } from 'react';
import { fmtDate } from '../lib/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => fmtDate(iso);

export const adaptArticle = (a, lang = 'en') => {
  const isGr = lang === 'gr';
  return {
    id: a.article_id,
    article_id: a.article_id,
    slug: a.slug,
    title: isGr ? (a.title_gr || a.title) : a.title,
    excerpt: isGr ? (a.excerpt_gr || a.excerpt) : a.excerpt,
    body: isGr ? (a.body_gr || a.body) : a.body,
    image: a.cover_image || '',
    category: a.category || 'news',
    published: !!a.published,
    display_order: a.display_order || 0,
    date: formatDate(a.published_at || a.created_at),
    tags: a.tags || [],
    raw: a,
  };
};

export const useArticles = (category, lang = 'en') => {
  const [articles, setArticles] = useState(null);
  const load = useCallback(async () => {
    const url = `${API}/api/cms/articles${category && category !== 'all' ? `?category=${category}` : ''}`;
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error('load failed');
      const d = await r.json();
      setArticles((d.articles || []).map((a) => adaptArticle(a, lang)));
    } catch {
      setArticles([]);
    }
  }, [category, lang]);
  useEffect(() => { load(); }, [load]);
  return { articles };
};

export const useGalleries = (lang = 'en') => {
  const [galleries, setGalleries] = useState(null);
  useEffect(() => {
    const isGr = lang === 'gr';
    fetch(`${API}/api/cms/galleries`)
      .then((r) => r.json())
      .then((d) => setGalleries((d.galleries || []).map((g) => ({
        id: g.gallery_id,
        title: isGr ? (g.title_gr || g.title) : g.title,
        description: isGr ? (g.description_gr || g.description) : g.description,
        cover_image: g.cover_image,
        images: g.images || [],
      }))))
      .catch(() => setGalleries([]));
  }, [lang]);
  return { galleries };
};

export const useResources = (lang = 'en') => {
  const [resources, setResources] = useState(null);
  useEffect(() => {
    const isGr = lang === 'gr';
    fetch(`${API}/api/cms/resources`)
      .then((r) => r.json())
      .then((d) => setResources((d.resources || []).map((r) => ({
        id: r.resource_id,
        resource_id: r.resource_id,
        title: isGr ? (r.title_gr || r.title) : r.title,
        description: isGr ? (r.description_gr || r.description) : r.description,
        file_url: r.file_url,
        category: r.category,
        trip_id: r.trip_id,
        kind: r.kind,
        archive_key: r.archive_key,
      }))))
      .catch(() => setResources([]));
  }, [lang]);
  return { resources };
};

export const useClimbingRoutes = (crag, lang = 'en') => {
  const [routes, setRoutes] = useState(null);
  useEffect(() => {
    const isGr = lang === 'gr';
    const url = `${API}/api/cms/climbing-routes${crag ? `?crag=${encodeURIComponent(crag)}` : ''}`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => setRoutes((d.routes || []).map((r) => ({
        id: r.route_id,
        crag: r.crag,
        name: isGr ? (r.name_gr || r.name) : r.name,
        grade: r.grade,
        length_m: r.length_m,
        bolts: r.bolts,
        style: r.style,
        description: isGr ? (r.description_gr || r.description) : r.description,
        topo_image: r.topo_image,
        coords: r.coords,
      }))))
      .catch(() => setRoutes([]));
  }, [crag, lang]);
  return { routes };
};

export const useAbout = (lang = 'en') => {
  const [about, setAbout] = useState(null);
  useEffect(() => {
    const isGr = lang === 'gr';
    fetch(`${API}/api/cms/about`)
      .then((r) => r.json())
      .then((d) => setAbout({
        body: isGr ? (d.body_gr || d.body) : d.body,
        history: isGr ? (d.history_gr || d.history) : d.history,
        mission: isGr ? (d.mission_gr || d.mission) : d.mission,
        contact_email: d.contact_email,
        contact_phone: d.contact_phone,
        address: d.address,
        facebook_url: d.facebook_url,
        instagram_url: d.instagram_url,
        raw: d,
      }))
      .catch(() => setAbout({}));
  }, [lang]);
  return { about };
};


/**
 * Static-page reader. Returns `{ blocks }` from `cms_singletons` keyed by
 * slug. Unedited pages return `{ blocks: {} }` so callers fall back to their
 * hardcoded copy. Pair with `readBlock(pageBlocks, slug, blockKey, lang)`
 * from `components/cms/pageSchemas` to merge defaults.
 */
export const usePage = (slug) => {
  const [page, setPage] = useState(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetch(`${API}/api/cms/pages/${slug}`)
      .then((r) => (r.ok ? r.json() : { slug, blocks: {} }))
      .then((d) => { if (!cancelled) setPage(d || { slug, blocks: {} }); })
      .catch(() => { if (!cancelled) setPage({ slug, blocks: {} }); });
    return () => { cancelled = true; };
  }, [slug]);
  return { page };
};
